import React, { useState, useEffect } from "react";
import SEO from "../component/SEO";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { businessInfo } from "../config/business";

const Orders = () => {
  const user = useSelector((state) => state.user);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchOrders = async (pageNum, isInitial = false) => {
    if (isInitial) setLoading(true);
    else setLoadingMore(true);

    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_DOMIN}/orders?page=${pageNum}&limit=10`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });
      const data = await res.json();
      if (!res.ok) toast.error(data.message || "Failed to fetch orders");
      else {
        const newOrders = data.orders || [];
        setOrders(prev => isInitial ? newOrders : [...prev, ...newOrders]);
        setHasMore(data.hasMore);
      }
    } catch (err) {
      console.error("❌ Fetch orders error:", err);
      toast.error("Server error while fetching orders");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!user.token) {
      toast.error("Please login to view your orders.");
      return;
    }
    fetchOrders(1, true);
  }, [user.token]);

  const handleScroll = () => {
    if (
      window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight - 100 &&
      hasMore &&
      !loadingMore &&
      !loading
    ) {
      setPage((prev) => {
        const nextPage = prev + 1;
        fetchOrders(nextPage);
        return nextPage;
      });
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasMore, loadingMore, loading]);

  const renderOrderStatus = (status) => {
    const colors = {
      pending: "bg-yellow-200 text-yellow-800",
      preparing: "bg-orange-200 text-orange-800",
      ready: "bg-blue-200 text-blue-800",
      completed: "bg-green-200 text-green-800",
      cancelled: "bg-red-200 text-red-800",
    };
    return <span className={`px-3 py-1 rounded-full text-sm font-semibold ${colors[status] || "bg-gray-200 text-gray-800"}`}>{status}</span>;
  };

  const renderItems = (items) =>
    items.map((item) => (
      <div key={item._id} className="flex justify-between border-b py-2">
        <div>
          <p className="font-semibold">{item.name}</p>
          <p className="text-gray-500 text-sm">
            {item.qty} x CAD {item.price.toFixed(2)}
          </p>
          {item.selectedOptions && Object.keys(item.selectedOptions).length > 0 && (
            <p className="text-gray-400 text-xs mt-1">
              Options:{" "}
              {Object.entries(item.selectedOptions)
                .map(([title, choice]) =>
                  Array.isArray(choice)
                    ? choice.map((c) => c.name).join(", ")
                    : choice.name
                )
                .join("; ")}
            </p>
          )}
        </div>
        <p className="font-bold">CAD {(item.qty * item.price).toFixed(2)}</p>
      </div>
    ));

  if (loading) {
    return <p className="text-center mt-10 text-gray-600">Loading your orders...</p>;
  }

  if (!orders.length) {
    return (
      <div className="flex flex-col items-center justify-center py-10">
        <p className="text-gray-600 text-lg">You have no orders yet! 🛒</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 bg-gradient-to-r from-pink-50 via-orange-50 to-yellow-50 min-h-screen">
      <SEO title="Your Orders - K Bakery" description="View your past and current orders at K Bakery." />
      <h1 className="text-3xl font-bold text-pink-700 mb-8 text-center">Your Orders 🍰</h1>
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {orders.map((order) => (
          <div key={order._id} className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-2xl transition">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-pink-700">Order #{order._id.slice(-6)}</h3>
              {renderOrderStatus(order.deliveryStatus)}
            </div>

            <div className="space-y-2">
              {renderItems(order.items)}
            </div>

            <div className="mt-4 ">
              <p className="text-gray-600 text-sm">Delivery Type: <span className="font-semibold">{order.deliveryType}</span></p>
<p className="text-gray-600 text-sm">
  Delivery / Pickup Date:{" "}
  <span className="font-semibold">
    {order.pickupDate
      ? new Date(order.pickupDate).toLocaleDateString()
      : order.deliveryDate
      ? new Date(order.deliveryDate).toLocaleDateString()
      : "N/A"}
  </span>
</p>
<p className="text-gray-600 text-sm">
  Delivery / Pickup Time:{" "}
  <span className="font-semibold">
  {(() => {
    const timeStr = order.pickupTime || order.deliveryTime;
    if (!timeStr) return "N/A";

    // Split hours and minutes
    const [hourStr, minStr] = timeStr.split(":");
    let hour = parseInt(hourStr, 10);
    const min = minStr;
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12;
    if (hour === 0) hour = 12;

    return `${hour}:${min} ${ampm}`;
  })()}
</span>

</p>

              {order.deliveryType === "delivery" && order.deliveryAddress && (
                <div className="mt-1 text-gray-600 text-sm">
                  <p>{order.deliveryAddress.recipientName}</p>
                  <p>{order.deliveryAddress.addressLine1}, {order.deliveryAddress.houseNumber}</p>
                  <p>{order.deliveryAddress.postalCode}</p>
                </div>
              )}
              <p className="font-bold mt-2 text-gray-800">Total: CAD {order.total.toFixed(2)}</p>
              <p className="text-gray-400 text-xs mt-1">Ordered on: {new Date(order.createdAt).toLocaleString()}</p>
            </div>
 {order.deliveryType === "pickup" && (
              <div className="mt-4">
                <h3 className="font-semibold text-lg text-pink-700 mb-2">
                  Pickup Location
                </h3>
                <p className="text-gray-600 mb-2">
                  You can pick up your order from our bakery at the following location:
                </p>
                <iframe
                  title="Bakery Location"
                  src={businessInfo.mapEmbedUrl}
                   className="w-full h-32 rounded-xl border"
                  allowFullScreen
                  loading="lazy"
                ></iframe>
                <p className="text-gray-500 mt-2">
                  {/* Visit us during our working hours to pick up your fresh bakery goodies! 🍰 */}
                </p>
              </div>
            )}

            {order.deliveryType === "delivery" && order.deliveryAddress?.location && (
              <div className="mt-4">
                <iframe
                  title="Delivery Location"
                  src={`https://maps.google.com/maps?q=${order.deliveryAddress.location.lat},${order.deliveryAddress.location.lng}&z=15&output=embed`}
                  className="w-full h-32 rounded-xl border"
                  allowFullScreen
                  loading="lazy"
                ></iframe>
              </div>
            )}
          </div>
        ))}
      </div>
      {loadingMore && (
        <p className="text-center mt-6 text-gray-500 animate-pulse">
          Loading more orders...
        </p>
      )}
    </div>
  );
};

export default Orders;
